import { createFileRoute, Link } from '@tanstack/react-router';
import { NavigationIcon, SearchIcon, SlidersHorizontalIcon, UsersIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { EmptyState, ListSkeleton } from '@/components/states';
import { CollectibilityBadge, DpdBadge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/form';
import { Sheet } from '@/components/ui/sheet';
import { distance, integer, rupiahCompact } from '@/lib/format';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useGeolocation } from '@/lib/use-geolocation';
import { cn } from '@/lib/utils';
import type { LoanFilters, LoanListItem, LoanSort } from '@/offline/queries';
import { queryLoans, searchLoanIds } from '@/offline/queries';
import { useOfflineQuery } from '@/offline/use-offline-query';

export const Route = createFileRoute('/_app/nasabah/')({
  component: CustomerListPage,
});

const PAGE_SIZE = 20;

const SORT_LABELS: Record<LoanSort, string> = {
  dpd: 'DPD terlama',
  outstanding: 'Outstanding terbesar',
  due: 'Jatuh tempo terdekat',
  distance: 'Terdekat dari saya',
};

/**
 * Daftar nasabah offline (T-P7-11/12/13, FR-CUST-01..07).
 *
 * "Rute Hari Ini" bukan mode terpisah melainkan pengurutan menurut jarak: daftar,
 * penyaring, dan pencarian yang sama tetap berlaku. Membuat layar tersendiri
 * berarti collector harus memilih lebih dulu antara mencari nasabah dan menyusun
 * rute — padahal di lapangan keduanya terjadi bergantian.
 */
function CustomerListPage() {
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebouncedValue(term);
  const [sort, setSort] = useState<LoanSort>('dpd');
  const [filters, setFilters] = useState<LoanFilters>({});
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  const { status: geoStatus, reading, locate } = useGeolocation();
  const routeMode = sort === 'distance';

  useEffect(() => {
    if (routeMode && !reading && geoStatus === 'idle') locate();
  }, [routeMode, reading, geoStatus, locate]);

  const { data: restrictToLoanIds } = useOfflineQuery(
    () => searchLoanIds(debouncedTerm),
    [debouncedTerm],
  );

  const filterKey = JSON.stringify(filters);
  const origin = routeMode ? (reading?.position ?? null) : null;

  const { data, loading } = useOfflineQuery(
    () =>
      queryLoans({
        filters,
        sort,
        origin,
        limit,
        restrictToLoanIds: restrictToLoanIds ?? undefined,
      }),
    [filterKey, sort, limit, restrictToLoanIds, origin?.lat, origin?.lng],
  );

  // Kata kunci baru selalu mengembalikan daftar ke halaman pertama; menyisakan
  // limit lama membuat hasil pertama pencarian tampak sudah tergulir jauh.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ketiganya adalah pemicu reset, bukan nilai yang dibaca efek.
  useEffect(() => setLimit(PAGE_SIZE), [debouncedTerm, filterKey, sort]);

  const sentinel = useInfiniteScroll(() => {
    if (data && data.items.length < data.total) setLimit((current) => current + PAGE_SIZE);
  });

  const activeFilterCount =
    (filters.collectibility?.length ? 1 : 0) +
    (filters.dpdMin ? 1 : 0) +
    (filters.hideVisitedToday ? 1 : 0);

  return (
    <div className="space-y-3 px-4 py-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon
            className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            inputMode="search"
            aria-label="Cari nama, rekening, HP, atau CIF"
            placeholder="Cari nama / rekening / HP"
            className="pl-9"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>

        <Button
          variant="secondary"
          onClick={() => setFilterOpen(true)}
          aria-label={`Filter${activeFilterCount > 0 ? ` (${activeFilterCount} aktif)` : ''}`}
          className="shrink-0 px-3"
        >
          <SlidersHorizontalIcon className="size-4" aria-hidden="true" />
          {activeFilterCount > 0 ? <span className="text-xs">{activeFilterCount}</span> : null}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Select
          aria-label="Urutkan"
          value={sort}
          onChange={(event) => setSort(event.target.value as LoanSort)}
          className="flex-1"
        >
          {(Object.keys(SORT_LABELS) as LoanSort[]).map((key) => (
            <option key={key} value={key}>
              {SORT_LABELS[key]}
            </option>
          ))}
        </Select>

        {routeMode ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <NavigationIcon className="size-3.5" aria-hidden="true" />
            {geoStatus === 'locating'
              ? 'Mencari lokasi…'
              : reading
                ? `±${Math.round(reading.accuracyM)} m`
                : 'Lokasi tidak tersedia'}
          </span>
        ) : null}
      </div>

      {debouncedTerm.length > 0 && debouncedTerm.length < 3 ? (
        <p className="text-xs text-muted-foreground">Ketik minimal 3 huruf untuk mencari.</p>
      ) : null}

      {loading && !data ? (
        <ListSkeleton />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title="Tidak ada nasabah yang cocok"
          description={
            debouncedTerm.length >= 3
              ? 'Coba kata kunci lain — pencarian offline mencocokkan dari awal kata.'
              : 'Ubah filter atau tarik data terbaru dari menu sinkronisasi.'
          }
        />
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {integer(data.items.length)} dari {integer(data.total)} pinjaman
          </p>

          <ul className="space-y-2">
            {data.items.map((item) => (
              <li key={item.loanId}>
                <LoanRow item={item} showDistance={routeMode} />
              </li>
            ))}
          </ul>

          <div ref={sentinel} className="h-8" aria-hidden="true" />
        </>
      )}

      <FilterSheet
        open={filterOpen}
        filters={filters}
        onClose={() => setFilterOpen(false)}
        onApply={(next) => {
          setFilters(next);
          setFilterOpen(false);
        }}
      />
    </div>
  );
}

function LoanRow({ item, showDistance }: { item: LoanListItem; showDistance: boolean }) {
  return (
    <Link
      to="/nasabah/$loanId"
      params={{ loanId: item.loanId }}
      className="block rounded-card border border-border bg-surface p-3 active:bg-muted"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="truncate font-medium">{item.customerName}</p>
          <p className="truncate text-xs text-muted-foreground">{item.loanNumber}</p>
        </div>
        <p className="shrink-0 text-right text-sm font-semibold tabular-nums">
          {rupiahCompact(item.arrears)}
          <span className="block text-xs font-normal text-muted-foreground">tunggakan</span>
        </p>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <CollectibilityBadge value={item.collectibility} />
        <DpdBadge days={item.dpd} />
        {showDistance ? (
          <span
            className={cn(
              'text-xs',
              item.distanceM === null ? 'text-muted-foreground' : 'text-foreground',
            )}
          >
            {item.distanceM === null ? 'Tanpa koordinat' : distance(item.distanceM)}
          </span>
        ) : null}
      </div>
    </Link>
  );
}

function FilterSheet({
  open,
  filters,
  onClose,
  onApply,
}: {
  open: boolean;
  filters: LoanFilters;
  onClose: () => void;
  onApply: (filters: LoanFilters) => void;
}) {
  const [draft, setDraft] = useState(filters);

  useEffect(() => setDraft(filters), [filters]);

  const toggleCollectibility = (value: number) => {
    const current = new Set(draft.collectibility ?? []);
    if (current.has(value)) current.delete(value);
    else current.add(value);
    setDraft({ ...draft, collectibility: [...current] });
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Filter nasabah"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={() => setDraft({})}>
            Reset
          </Button>
          <Button fullWidth onClick={() => onApply(draft)}>
            Terapkan
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Kolektibilitas</legend>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5].map((value) => {
              const active = draft.collectibility?.includes(value) ?? false;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleCollectibility(value)}
                  className={cn(
                    'rounded-card border px-3 py-2 text-sm',
                    active ? 'border-brand bg-brand/10 text-brand' : 'border-border',
                  )}
                >
                  Kol {value}
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">DPD minimal</legend>
          <div className="flex flex-wrap gap-2">
            {[0, 1, 31, 61, 91].map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={(draft.dpdMin ?? 0) === value}
                onClick={() => setDraft({ ...draft, dpdMin: value === 0 ? undefined : value })}
                className={cn(
                  'rounded-card border px-3 py-2 text-sm',
                  (draft.dpdMin ?? 0) === value
                    ? 'border-brand bg-brand/10 text-brand'
                    : 'border-border',
                )}
              >
                {value === 0 ? 'Semua' : `${value}+ hari`}
              </button>
            ))}
          </div>
        </fieldset>

        <label className="flex min-h-touch items-center gap-3 text-sm">
          <input
            type="checkbox"
            className="size-5 accent-[var(--color-brand)]"
            checked={draft.hideVisitedToday ?? false}
            onChange={(event) => setDraft({ ...draft, hideVisitedToday: event.target.checked })}
          />
          Sembunyikan yang sudah dikunjungi hari ini
        </label>
      </div>
    </Sheet>
  );
}

/** Muat halaman berikutnya saat sentinel masuk viewport. */
function useInfiniteScroll(onReach: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const callback = useRef(onReach);
  callback.current = onReach;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) callback.current();
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return ref;
}
